import { compareAsc } from "date-fns";

import { Capability, hasSectionCapability } from "../../shared/capabilities";

import {
  activitySelectors,
  createAppSelector,
  pageOutcomeSelectors,
  pageSelectors,
  selectCurrentSectionId,
  topicSelectors,
} from "./base";
import { selectAllAssessmentInfo } from "./computed";
import { selectCurrentUserComputedRole } from "./permissions";
import { selectAuthorizedPages, selectPagesForCurrentSection } from "./section";
import { getSortedPages, sortActivities, sortPagesForTopic, sortTopicActivities } from "./sorting";
import { ActivityWithPagesType, PageWithDueDateType, TopicWithActivitiesType } from "./types";

import { AssessmentStatus } from "api/api-data-types";
import { isDateWithinLastXDays } from "components/materials/page/helpers";
import { ActivityPageType, PageType, TopicType } from "components/server-types";
import { DEFAULT_ORDER_FN } from "mds/hooks/use-ordered";
import { isSectionPageCategory } from "utils/page-utils";
import { HomeRole } from "utils/urls";

// This is different from selectPagesForCurrentSection in that it also selects topics-related pages
export const selectAllPagesWithDueDates = createAppSelector(
  [selectAuthorizedPages],
  (pages) => pages.filter((page) => page.due_at) as PageWithDueDateType[],
);

export const selectAllRecentlyAssessedPages = createAppSelector(
  [
    (_state, daysAgo: number) => daysAgo,
    (state, _daysAgo: number, homeRole?: HomeRole | null) => selectAuthorizedPages(state, homeRole),
  ],
  (daysAgo, pages) =>
    pages
      .filter((page) => isDateWithinLastXDays(page.assessments_published_at, daysAgo))
      .sort((a, b) =>
        compareAsc(a.assessments_published_at ?? "", b.assessments_published_at ?? ""),
      ),
);

export const selectReadyForAssessmentPages = createAppSelector(
  [selectAuthorizedPages, selectAllAssessmentInfo],
  (pages, statuses) =>
    pages
      .filter((page) => statuses[page.id]?.status === AssessmentStatus.ASSESSABLE)
      .sort((a, b) => compareAsc(a.due_at ?? "", b.due_at ?? "")),
);

export const selectPagesForTopic = createAppSelector(
  [(state, topicId: string) => topicId, pageSelectors.selectAll, (state) => state.activities],
  (topicId, pages, activities) => {
    return pages.filter((page) => {
      if (!page.activity_id) return false;
      const activity = activities[page.activity_id];
      return activity && activity.topic_id === topicId;
    });
  },
);

export const selectPagesForOutcome = createAppSelector(
  [
    (state, sectionOutcomeId: string) => sectionOutcomeId,
    pageSelectors.selectAll,
    pageOutcomeSelectors.selectAll,
  ],
  (sectionOutcomeId, pages, pageOutcomes) => {
    return pages.filter((page) =>
      pageOutcomes.some(
        (po) => po.section_outcome_id === sectionOutcomeId && po.page_id === page.id,
      ),
    );
  },
);

export const selectChunkedPagesForOutcomeByTopic = createAppSelector(
  [
    (state, sectionOutcomeId: string) => selectPagesForOutcome(state, sectionOutcomeId),
    activitySelectors.selectAll,
    topicSelectors.selectAll,
  ],
  (pages, activities, topics) => {
    const byTopic = topics.map((topic) => {
      const topicPages = pages.filter((page) => {
        const activity = activities.find((act) => act.id === page.activity_id);
        return activity?.topic_id === topic.id;
      });
      return { ...topic, pages: topicPages };
    });
    return byTopic;
  },
);

/**
 * This selector is very similar to `topicSelectors.filterForCurrentSection`, but it filters out topics
 * that should not be visible to the current user.
 * For instructors, the result of this selector is identical to `topicSelectors.filterForCurrentSection`.
 * But for students, this selector will filter out topics that have no released pages.
 */
export const selectVisibleTopicsForCurrentSection = createAppSelector(
  [
    pageSelectors.selectAll,
    activitySelectors.selectEntities,
    topicSelectors.filterForCurrentSection,
    selectCurrentUserComputedRole,
  ],
  (pages: PageType[], activities, topicsForCurrentSection: TopicType[], computedRole) => {
    if (hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION)) {
      return topicsForCurrentSection;
    }

    const releasedPages = pages.filter((page) => page.released_at);

    return topicsForCurrentSection.filter((topic) => {
      // Topics have a `released_pages_count` attribute, but we can't rely on it,
      // because it's not updated when pages are released/unreleased.
      // Instead, we'll see if the topic has any pages that are released.
      const topicHasReleasedPages = releasedPages.some((page) => {
        if (!page.activity_id) return false;
        const activity = activities[page.activity_id];
        return activity && activity.topic_id === topic.id;
      });
      return topicHasReleasedPages;
    });
  },
);

export const selectVisibleTopicsWithPages = createAppSelector(
  [
    pageSelectors.selectAll,
    activitySelectors.selectAll,
    topicSelectors.filterForCurrentSection,
    selectCurrentUserComputedRole,
  ],
  (pages, activities, topicsForCurrentSection, computedRole) => {
    const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    const topicsWithPages = topicsForCurrentSection.map((topic) => {
      // Topics have a `released_pages_count` attribute, but we can't rely on it,
      // because it's not updated when pages are released/unreleased.
      // Instead, we'll see if the topic has any pages that are released.
      const topicPages = sortPagesForTopic(topic.id, activities, pages);
      const released_page_count = topicPages.filter((page) => page.released_at).length;
      const unreleased_page_count = topicPages.filter((page) => !page.released_at).length;

      return {
        ...topic,
        pages: topicPages,
        released_page_count,
        unreleased_page_count,
      };
    });

    return canAuthorSection
      ? topicsWithPages
      : topicsWithPages.filter((t) => t.released_page_count > 0);
  },
);

export const selectSortedTopicActivitiesByTopicId = createAppSelector(
  [
    (state, topicId: string) => topicId,
    activitySelectors.selectAll,
    pageSelectors.selectAll,
    selectCurrentUserComputedRole,
  ],
  (topicId, activities, pages, computedRole) => {
    const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    return sortTopicActivities(topicId, activities, pages, canAuthorSection);
  },
);

export const selectedSortedTopicActivitiesByPageId = createAppSelector(
  [
    (state, pageId: string) => pageId,
    activitySelectors.selectAll,
    pageSelectors.selectAll,
    selectCurrentUserComputedRole,
  ],
  (pageId, activities, pages, computedRole) => {
    const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    if (!pageId) return [];

    const foundPage = pages.find((page) => page.id === pageId);
    if (!foundPage) return [];

    const foundActivity = activities.find((activity) => activity.id === foundPage.activity_id);
    if (!foundActivity) return [];

    return sortTopicActivities(foundActivity.topic_id, activities, pages, canAuthorSection);
  },
);

export const selectSortedTopicActivitiesForCurrentSection = createAppSelector(
  [
    (state, pageFilter: (page: ActivityPageType) => boolean) => pageFilter,
    topicSelectors.filterForCurrentSection,
    activitySelectors.selectAll,
    pageSelectors.selectAll,
    selectCurrentUserComputedRole,
  ],
  (pageFilter, topics, activities, pages, computedRole) => {
    const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    return topics.reduce((topicsAccum, topic) => {
      const sortedActivities = activities
        .filter((activity) => activity.topic_id === topic.id)
        .sort(sortActivities);

      const activityWithPages = sortedActivities.reduce((activitiesAccum, activity) => {
        const activityPages = pages.filter(
          (page) =>
            page.activity_id === activity.id &&
            (canAuthorSection || page.released_at) &&
            (pageFilter ? pageFilter(page as unknown as ActivityPageType) : true),
        );

        if (activityPages.length > 0) {
          activitiesAccum.push({ ...activity, pages: activityPages } as ActivityWithPagesType);
        }
        return activitiesAccum;
      }, [] as ActivityWithPagesType[]);

      if (activityWithPages.length > 0) {
        topicsAccum.push({ ...topic, activities: activityWithPages });
      }
      return topicsAccum;
    }, [] as TopicWithActivitiesType[]);
  },
);

export const selectPageViewData = createAppSelector(
  [
    (state, page: PageType | null) => page,
    (state, page: PageType | null, topicIdParam?: string | null) => topicIdParam,
    (state, page: PageType | null) => selectPagesForCurrentSection(state, page?.category),
    (state, page: PageType | null) => selectedSortedTopicActivitiesByPageId(state, page?.id ?? ""),
    topicSelectors.filterForCurrentSection,
    selectCurrentSectionId,
    activitySelectors.selectAll,
    pageSelectors.selectAll,
    selectCurrentUserComputedRole,
  ],
  (
    page,
    topicIdParam,
    sectionPages,
    topicActivities,
    topics,
    currentSectionId,
    allActivities,
    allPages,
    computedRole,
  ) => {
    const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    const topicFromQuery = topics.find((topic) => topic.id === topicIdParam);
    const topicId = topicActivities[0]?.topic_id || topicFromQuery?.id || topics[0]?.id;

    const defaultTopicActivities = sortTopicActivities(
      topicId,
      allActivities,
      allPages,
      canAuthorSection,
    );
    const defaultActivityPages = getSortedPages(defaultTopicActivities);

    if (!page || (currentSectionId && page.section_id !== currentSectionId)) {
      return {
        selectedPage: null,
        pages: defaultActivityPages,
        topicId,
      };
    }

    const activityPages = getSortedPages(topicActivities);
    const orderedSectionPages = sectionPages.toSorted(DEFAULT_ORDER_FN);
    const pages = page?.category === "activity_page" ? activityPages : orderedSectionPages;

    // Account for the case where a user is viewing a page that they have access to and then loses
    // their access to it, e.g., due to the page being unreleased or the user's role changing.
    if (page && !pages.some((p) => p.id === page.id)) {
      return { selectedPage: null, pages, topicId };
    }

    return { selectedPage: page, pages, topicId };
  },
);

export const selectCurrentSectionHasMaterials = createAppSelector(
  [selectPagesForCurrentSection, topicSelectors.filterForCurrentSection],
  (pages, topics) => {
    if (topics.length > 0) {
      return true;
    }
    return pages.filter((p) => isSectionPageCategory(p.category)).length > 0;
  },
);
